import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import logoIvi from "../../assets/logo.svg";
import ButtonComponent from "../../components/ButtonComponent";
import LoadingFullPage from "../../components/Loading/LoadingFullPage/LoadingFullPage";
import { Respostas, RespostaValor, SurveyRenderer, perguntaVisivel } from "../../components/survey/SurveyRenderer";

import {
  ConvitePesquisa,
  ItemResposta,
  getPesquisaPorConvite,
  registrarRespostaPorConvite,
} from "../../services/pesquisa";
import { GET_PESQUISA_CONVITE, POST_PESQUISA_RESPOSTA_CONVITE } from "../../query/constants";

export const PesquisaPublica = () => {
  const { token } = useParams<{ token: string }>();

  const [respostas, setRespostas] = useState<Respostas>({});
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const [concluida, setConcluida] = useState(false);

  const { data: convite, isLoading, isError } = useQuery<ConvitePesquisa, Error>({
    queryKey: [GET_PESQUISA_CONVITE, token],
    queryFn: () => getPesquisaPorConvite(token!),
    enabled: Boolean(token),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const enviar = useMutation({
    mutationKey: [POST_PESQUISA_RESPOSTA_CONVITE],
    mutationFn: (itens: ItemResposta[]) => registrarRespostaPorConvite(token!, itens),
    onSuccess: () => setConcluida(true),
  });

  const perguntas = useMemo(() => convite?.questionario?.perguntas ?? [], [convite]);

  const handleChange = useCallback((chave: string, valor: RespostaValor) => {
    setRespostas((atuais) => ({ ...atuais, [chave]: valor }));
  }, []);

  const faltamObrigatorias = useMemo(
    () =>
      perguntas
        .filter((p) => p.obrigatoria && perguntaVisivel(p, respostas))
        .some((p) => {
          const valor = respostas[p.chave];
          return valor === null || valor === undefined || valor === "";
        }),
    [perguntas, respostas]
  );

  const handleEnviar = useCallback(() => {
    setTentouEnviar(true);
    if (faltamObrigatorias) return;

    const itens: ItemResposta[] = [];

    perguntas.forEach((pergunta) => {
      if (!perguntaVisivel(pergunta, respostas)) return;

      const valor = respostas[pergunta.chave];
      if (valor === null || valor === undefined || valor === "") return;

      itens.push({
        chave: pergunta.chave,
        valorNumerico: typeof valor === "number" ? valor : null,
        valorTexto: typeof valor === "string" ? valor : null,
        valorBooleano: typeof valor === "boolean" ? valor : null,
      });
    });

    enviar.mutate(itens);
  }, [faltamObrigatorias, perguntas, respostas, enviar]);

  if (isLoading) return <LoadingFullPage />;

  if (isError || !convite) {
    return (
      <Mensagem
        titulo="Link indisponível"
        texto="Este link de pesquisa não está mais válido. Se você recebeu outro e-mail mais recente, use o link de lá."
      />
    );
  }

  if (concluida) {
    return <Mensagem titulo="Obrigado!" texto="Sua resposta foi registrada e vai direto para o nosso time." />;
  }

  return (
    <Moldura>
      <h1 className="text-2xl font-bold text-zinc-800 mb-2">{convite.questionario.titulo}</h1>
      {convite.questionario.descricao && (
        <p className="text-sm text-zinc-600 mb-8">{convite.questionario.descricao}</p>
      )}

      <SurveyRenderer perguntas={perguntas} respostas={respostas} onChange={handleChange} />

      {tentouEnviar && faltamObrigatorias && (
        <p className="text-sm text-red-600 mt-4" role="alert">
          Responda as perguntas obrigatórias para enviar.
        </p>
      )}

      {enviar.isError && (
        <p className="text-sm text-red-600 mt-4" role="alert">
          Não conseguimos registrar sua resposta agora. Tente novamente em instantes.
        </p>
      )}

      <div className="mt-8">
        <ButtonComponent className="continue-button" isDisabled={enviar.isPending} onClick={handleEnviar}>
          Enviar
        </ButtonComponent>
      </div>

      {enviar.isPending && <LoadingFullPage />}
    </Moldura>
  );
};

const Moldura = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen w-full flex justify-center bg-[#f7f7f7] font-kumbh-sans">
    <div className="w-full max-w-[520px] px-5 py-10">
      <img src={logoIvi} alt="IVI Energia" className="h-8 mb-10" />
      {children}
    </div>
  </div>
);

const Mensagem = ({ titulo, texto }: { titulo: string; texto: string }) => (
  <Moldura>
    <h1 className="text-2xl font-bold text-zinc-800 mb-3">{titulo}</h1>
    <p className="text-sm text-zinc-600">{texto}</p>
  </Moldura>
);
